<template>
  <div class="zahlungsvorgang-info">
    <Heading
      size="lg"
      align="center"
      spaces-bottom-rem
    >
      Zahlungsvorgang
    </Heading>
    <Paragraph
      v-if="cartData?.jointCart"
      align="center"
    >
      Der Verkauf und Versand der von dir gewählten Produkte erfolgt über zwei Anbieter, daher muss
      auch die Zahlung in zwei Schritten erfolgen.
    </Paragraph>

    <div class="zahlungsvorgang-info__tiles">
      <PaymentProcessTile
        v-if="showTelekom"
        provider="TELEKOM"
        :is-payment-possible="isTelekomPayable"
        :is-successful="cartData.paidTelekom"
        :index="cartData?.jointCart ? 1 : undefined"
        class="zahlungsvorgang-info__tile"
      />

      <PaymentProcessTile
        v-if="showBrodos"
        provider="BRODOS"
        :is-payment-possible="isBrodosPayable"
        :is-successful="cartData.paidBrodos"
        :has-cancel="isBrodosCancelable"
        :index="cartData?.jointCart ? 2 : undefined"
        class="zahlungsvorgang-info__tile"
        @cancel="cancel"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from '#imports';
import { storeToRefs } from 'pinia';
import { computed, markRaw, defineAsyncComponent } from 'vue';
import { useCartStore } from '~/stores/cart';
import { Paragraph, Heading } from '@i22/td-longtail-vue-components';
import PaymentProcessTile from '~/components/shop/payment-process-tile.vue';
import { openModal } from '~/composables/modal';
import ShopApi from '~/lib/shop-api';
import { captureException, captureMessage } from '~/lib/sentry';
import { getRouteByState } from '~/lib/route-cart-state';

const router = useRouter();

const cartStore = useCartStore();

const { cartData, getAnonymousCartData, cartState, cartInitialized } = storeToRefs(cartStore);

const isTelekomPayable = computed(() => ['telekom_payment_transferred'].includes(cartState.value));

const isBrodosPayable = computed(() => ['brodos_payment_transferred'].includes(cartState.value));

const showTelekom = computed(
  () => cartData.value?.jointCart || ['telekom_payment_transferred'].includes(cartState.value)
);
const showBrodos = computed(
  () => cartData.value?.jointCart || ['brodos_payment_transferred'].includes(cartState.value)
);
const isBrodosCancelable = computed(
  () =>
    cartData.value?.jointCart &&
    ['brodos_payment_transferred'].includes(cartState.value) &&
    cartData.value.paidTelekom
);

const doSplitCart = async () => {
  captureMessage('Split joint cart', {
    contexts: {
      cart: getAnonymousCartData.value,
    },
    tags: {
      orderKey: cartData.value?.orderKey,
      orderId: cartData.value?.id,
    },
  });

  try {
    const cart = await ShopApi.splitJointCart();
    if (!cart?.order?.state) {
      throw new Error('No cart data received');
    }

    const redirect = getRouteByState(cart.order);
    if (!redirect) {
      throw new Error('No redirect received');
    }

    router.push(redirect);
    return;
  } catch (error) {
    captureException(error, {
      contexts: {
        cart: getAnonymousCartData.value,
      },
      tags: {
        orderKey: cartData.value?.orderKey,
        orderId: cartData.value?.id,
      },
    });
  }

  router.push('/shop/zahlungsverifizierung');
};

const cancel = () => {
  openModal({
    closeOnBackdrop: true,
    heading: 'Teilbestellung wirklich stornieren?',
    component: {
      is: markRaw(defineAsyncComponent(() => import('~/components/shop/resolve-joint-cart.vue'))),
    },
    buttons: [
      {
        name: 'Schließen',
        outline: true,
        tag: 'button',
        type: 'button',
      },
      {
        name: 'Artikel stornieren',
        primary: true,
        tag: 'button',
        type: 'button',
        click: async () => {
          await doSplitCart();
        },
      },
    ],
  });
};

defineExpose({ doSplitCart });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.zahlungsvorgang-info {
  &__tiles {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 400px;
    margin-inline: auto;
    margin-block: 1rem;

    @include mq($mq-sm) {
      max-width: none;
      align-items: center;
    }

    @include mq($mq-md) {
      flex-direction: row;
      justify-content: center;
    }
  }

  &__tile {
    width: 100%;
  }
}
</style>
